import { useMemo, useState } from 'react';

import { useFormContext } from 'libs/form/useFormContext';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import {
  CountryRiskRole,
  getMainAddressCountryRisk,
} from 'modules/client/common/feature';

import { BeneficiaryItem } from './BeneficiariesInfoCard';
import { CompanyBeneficiariesSectionProps } from './types';

export const useCompanyBeneficiariesSection = ({
  unidentifiableBeneficiaryLetterFiles,
  beneficiaries,
  onClickBeneficiary,
  onClickAddBeneficiary,
  onArchiveBeneficiary,
  onDearchiveBeneficiary,
  onDeleteBeneficiary,
}: CompanyBeneficiariesSectionProps) => {
  const { readOnly } = useFormContext();

  const [dialogLoading, setDialogLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<BeneficiaryItem>();

  const {
    isOpen: isOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
    open: openDeleteDialog,
  } = useDialog();
  const {
    isOpen: isOpenArchiveDialog,
    onClose: onCloseArchiveDialog,
    open: openArchiveDialog,
  } = useDialog();

  const shouldShowAddButton = !readOnly && !!onClickAddBeneficiary;

  const hasBeneficiaries = !!beneficiaries.length;

  const hasUnidentifiableBeneficiaryLetterFiles =
    !!unidentifiableBeneficiaryLetterFiles?.length;

  const shouldShowUnidentifiableBeneficiaryContent =
    !hasBeneficiaries && hasUnidentifiableBeneficiaryLetterFiles;

  const beneficiariesItems: BeneficiaryItem[] = useMemo(() => {
    const result = beneficiaries.map((beneficiary) => {
      const countryRisk = getMainAddressCountryRisk(
        beneficiary.residentialCountry,
        beneficiary.registrationCountry
      );

      return {
        id: beneficiary.id,
        name: beneficiary.name || '',
        email: beneficiary.email || '',
        country: countryRisk.country?.name || '',
        countryRisk: {
          ...countryRisk,
          role: CountryRiskRole.LegalClientBeneficiary,
        },
        capitalParticipationPercentage:
          (beneficiary.directCapitalParticipationPercentage || 0) +
          (beneficiary.indirectCapitalParticipationPercentage || 0),
        archived: beneficiary.archived,
        createdAt: beneficiary.createdAt,
      };
    });

    result.sort((a, b) =>
      a.archived === b.archived
        ? b.createdAt.getTime() - a.createdAt.getTime()
        : a.archived
        ? 1
        : -1
    );

    return result;
  }, [beneficiaries]);

  const onClickDelete = onDeleteBeneficiary
    ? (row: BeneficiaryItem) => {
        setActiveItem(row);
        openDeleteDialog();
      }
    : undefined;

  const onClickArchive = onArchiveBeneficiary
    ? (row: BeneficiaryItem) => {
        setActiveItem(row);
        openArchiveDialog();
      }
    : undefined;

  const onDelete = async (reason: string) => {
    if (activeItem && onDeleteBeneficiary) {
      setDialogLoading(true);
      try {
        await onDeleteBeneficiary(activeItem.id, reason);
      } catch (error) {
        /* empty */
      }
      setDialogLoading(false);
    }
    onCloseDeleteDialog();
  };

  const onArchiveAndDearchive = async (reason: string) => {
    if (activeItem) {
      setDialogLoading(true);
      if (!activeItem.archived && onArchiveBeneficiary) {
        try {
          await onArchiveBeneficiary(activeItem.id, reason);
        } catch (error) {
          /* empty */
        }
      }

      if (activeItem.archived && onDearchiveBeneficiary) {
        try {
          await onDearchiveBeneficiary(activeItem.id, reason);
        } catch (error) {
          /* empty */
        }
      }

      onCloseArchiveDialog();
      setDialogLoading(false);
    }
  };

  return {
    hasBeneficiaries,
    dialogLoading,
    isOpenArchiveDialog,
    isOpenDeleteDialog,
    beneficiariesItems,
    hasUnidentifiableBeneficiaryLetterFiles,
    shouldShowUnidentifiableBeneficiaryContent,
    unidentifiableBeneficiaryLetterFiles,
    shouldShowAddButton,
    onClickArchive,
    onClickDelete,
    onClickBeneficiary,
    onClickAddBeneficiary,
    onCloseArchiveDialog,
    onCloseDeleteDialog,
    onArchiveAndDearchive,
    onDelete,
  };
};
